/**
 * Modal — SSOT for all dialog surfaces.
 *
 * Every modal in the app goes through this primitive. Sizes come from
 * the `--modal-w-*` tokens (see index.css). Lightweight confirmation
 * prompts should compose `<ConfirmDialog>` (in this folder) which
 * delegates to Modal with size='xs' + danger tone affordances.
 *
 * Contract:
 *   - Header/body/footer slots share one padding scale — callers never
 *     override spacing.
 *   - Backdrop click + Escape close by default; opt out with
 *     `closeOnBackdrop={false}` for destructive/loading mid-flight.
 *   - Focus trap + restore — tab stays inside the panel, closing
 *     returns focus to whatever had it before open.
 *   - iOS-safe body-scroll lock (no rubber-band).
 *   - `tone="danger"` tints the header bar so destructive flows stand
 *     out visually.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { MouseEvent, ReactNode } from 'react';
import { X } from 'lucide-react';
import { useKeyboardScope } from '../keyboard';
import type { KeyComboShortcut } from '../keyboard';
import { useSwipeGesture } from '../gestures/useSwipeGesture';
import { useScrollFade } from './useScrollFade';
import './Modal.css';

/** Modal width scale — maps to `--modal-w-*` tokens in index.css.
 *  xs (360) confirm dialogs, sm (440) simple actions, md (560) forms,
 *  lg (720) workflows, xl (960) advanced config/tables, fullscreen for
 *  rare complex flows. */
export type ModalSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'fullscreen';
/** Where to position the modal panel on desktop (≥1024px).
 *  Mobile always renders as a centered fullscreen sheet regardless. */
export type ModalPlacement = 'center' | 'right';
/** Default tone renders the header chrome neutrally; 'danger' tints it
 *  red for destructive flows (delete confirms, role removals, etc.). */
export type ModalTone = 'default' | 'danger';

export type ModalMode = 'centered' | 'sheet-partial' | 'sheet-full';

export type ModalProps = {
    open: boolean;
    onClose: () => void;
    children: ReactNode;
    size?: ModalSize;
    tone?: ModalTone;
    /** Accessible label — required for screen readers. */
    ariaLabel?: string;
    /** Id of the element that describes the dialog. Forwarded to the
     *  dialog root as `aria-describedby` so SR users hear the
     *  consequence detail (not just the title). */
    ariaDescribedBy?: string;
    /** If false, clicking the backdrop does not close the modal. Default true. */
    closeOnBackdrop?: boolean;
    /** If false, pressing Escape does not close the modal. Default true.
     *  Use during loading/destructive confirms. */
    closeOnEscape?: boolean;
    /** Desktop placement. Default 'center'. 'right' renders a
     *  right-aligned drawer at ≥1024px. Ignored on mobile. */
    placement?: ModalPlacement;
    /** Layout mode. Default 'centered' — current behaviour, respects
     *  the `size` prop.
     *
     *  'sheet-partial' — bottom sheet anchored to the viewport floor;
     *  auto-height up to 70 vh; shows a drag-handle affordance at the
     *  top of the panel. The `size` prop is ignored (sheet is
     *  full-bleed width).
     *
     *  'sheet-full' — bottom sheet, 90 vh, no drag handle. `size` is
     *  also ignored.
     *
     *  Focus-trap, Escape, backdrop-click, and body-scroll-lock all
     *  behave identically across modes — the mode is a pure layout
     *  flag. */
    mode?: ModalMode;
};

type SlotProps = { children: ReactNode; className?: string };

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Open-state controller for Modal — mounts only while open, so the
 *  keyboard scope is pushed/popped strictly in lockstep with visibility.
 *  Pulled into a subcomponent so `useKeyboardScope` (which always pushes
 *  on mount) maps cleanly to the modal's open lifecycle. */
function ModalOpenController({
    panelRef,
    onClose,
    closeOnEscape,
}: {
    panelRef: React.RefObject<HTMLDivElement | null>;
    onClose: () => void;
    closeOnEscape: boolean;
}) {
    const previouslyFocused = useRef<HTMLElement | null>(null);

    // Focus the first focusable in the panel + body-scroll lock —
    // both have lifetimes equal to the open-state, so they live in
    // a single effect that mounts/unmounts with this controller.
    useEffect(() => {
        previouslyFocused.current = document.activeElement as HTMLElement | null;

        const panel = panelRef.current;
        const focusable = panel
            ? Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
            : [];
        focusable[0]?.focus();

        /* iOS-safe body scroll lock — overflow:hidden alone doesn't
         * stop Safari's rubber-band, so we also pin the body to the
         * current scroll position and restore on unmount.
         *
         * Also lock `document.documentElement` (html) — some browsers
         * allow the page to scroll behind a fixed overlay when only
         * `body.overflow` is set. Locking both matches the Radix /
         * ARIA dialog convention. */
        const scrollY = window.scrollY;
        const body = document.body;
        const html = document.documentElement;
        const prev = {
            bodyOverflow: body.style.overflow,
            bodyPosition: body.style.position,
            bodyTop: body.style.top,
            bodyWidth: body.style.width,
            htmlOverflow: html.style.overflow,
        };
        body.style.overflow = 'hidden';
        body.style.position = 'fixed';
        body.style.top = `-${scrollY}px`;
        body.style.width = '100%';
        html.style.overflow = 'hidden';

        return () => {
            body.style.overflow = prev.bodyOverflow;
            body.style.position = prev.bodyPosition;
            body.style.top = prev.bodyTop;
            body.style.width = prev.bodyWidth;
            html.style.overflow = prev.htmlOverflow;
            window.scrollTo(0, scrollY);
            previouslyFocused.current?.focus();
        };
    }, [panelRef]);

    // Escape + Tab focus-trap shortcuts. Registered through the
    // keyboard SSOT so this scope owns the dispatch while open.
    // ESC is registered explicitly even when `closeOnEscape=false`,
    // with a no-op handler — the scope-stack's default ESC-pop must
    // NOT fire (it would tear down the modal's scope silently).
    //
    // The Tab handler is hoisted to its own useCallback so the ref
    // access (`panelRef.current`) is fenced inside an event-handler
    // closure rather than the useMemo body — satisfying the React 19
    // "no ref access during render" lint rule.
    const handleTab = useCallback((e: KeyboardEvent) => {
        const panel = panelRef.current;
        if (!panel) return;
        const current = Array.from(
            panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
        );
        if (current.length === 0) return;
        const first = current[0];
        const last = current[current.length - 1];
        if (!first || !last) return;
        if (e.shiftKey && document.activeElement === first) {
            e.preventDefault();
            last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
            e.preventDefault();
            first.focus();
        }
    }, [panelRef]);

    const shortcuts = useMemo<ReadonlyArray<KeyComboShortcut>>(() => [
        {
            combo: 'escape',
            handler: (e) => {
                if (!closeOnEscape) return;
                e.stopPropagation();
                onClose();
            },
        },
        { combo: 'tab', handler: handleTab },
    ], [closeOnEscape, onClose, handleTab]);

    useKeyboardScope(shortcuts, { owner: 'Modal' });
    return null;
}

function Modal({
    open,
    onClose,
    children,
    size = 'md',
    tone = 'default',
    ariaLabel,
    ariaDescribedBy,
    closeOnBackdrop = true,
    closeOnEscape = true,
    placement = 'center',
    mode = 'centered',
}: ModalProps) {
    const panelRef = useRef<HTMLDivElement>(null);

    // Swipe-to-dismiss for sheet-partial mode (per §5.5 / slice 89).
    // Disabled in every other mode so the hook costs nothing on the
    // common path. The Escape key + close (X) button remain the
    // canonical a11y dismissal paths — swipe is purely an enhancement.
    const { handlers: sheetSwipeHandlers } = useSwipeGesture({
        onSwipeDown: onClose,
        disabled: mode !== 'sheet-partial',
    });

    if (!open) return null;

    const handleBackdrop = (e: MouseEvent<HTMLDivElement>) => {
        if (!closeOnBackdrop) return;
        if (e.target === e.currentTarget) onClose();
    };

    const isSheet = mode === 'sheet-partial' || mode === 'sheet-full';

    // Sheet modes are full-bleed bottom-anchored panels — the `size`
    // prop's max-width is irrelevant and would fight the sheet layout,
    // so we omit the size class when a sheet mode is active.
    const panelSizeClass = isSheet ? '' : `ui-modal--${size}`;

    // Sheet modes use a dedicated overlay class (bottom-anchored flex),
    // otherwise fall back to the placement-driven class.
    const overlayClass = isSheet
        ? 'ui-modal-overlay--sheet'
        : `ui-modal-overlay--${placement}`;

    // Placement class is only meaningful for non-sheet centered/right layouts.
    const panelPlacementClass = isSheet ? '' : `ui-modal--${placement}`;

    const panelModeClass = isSheet ? `ui-modal--${mode}` : '';

    const panelClassName = [
        'ui-modal',
        panelSizeClass,
        panelPlacementClass,
        `ui-modal--tone-${tone}`,
        panelModeClass,
    ]
        .filter(Boolean)
        .join(' ');

    // Portal to <body> so the modal escapes any ancestor stacking
    // context — `mask-image` / `transform` / `filter` on a parent (e.g.
    // useScrollFade's mask on RailLayout's main pane) creates a
    // containing block for `position: fixed`, which would otherwise
    // trap the overlay inside that pane and let the rail/header sit on
    // top. Portalling sidesteps the trap entirely.
    return createPortal(
        <div
            className={`ui-modal-overlay ${overlayClass}`}
            onClick={handleBackdrop}
        >
            <div
                ref={panelRef}
                className={panelClassName}
                role="dialog"
                aria-modal="true"
                aria-label={ariaLabel}
                aria-describedby={ariaDescribedBy}
                onClick={(e) => e.stopPropagation()}
            >
                {mode === 'sheet-partial' && (
                    <div
                        className="ui-modal__drag-handle"
                        aria-hidden="true"
                        // Swipe-down on the drag handle dismisses the sheet.
                        // The X button + Escape stay as the canonical a11y
                        // dismissal paths — see useSwipeGesture.ts header
                        // for the a11y contract.
                        {...sheetSwipeHandlers}
                    />
                )}
                <ModalOpenController
                    panelRef={panelRef}
                    onClose={onClose}
                    closeOnEscape={closeOnEscape}
                />
                {children}
            </div>
        </div>,
        document.body,
    );
}

type HeaderProps = SlotProps & {
    onClose?: () => void;
    /** Optional short line beneath the title. Use sparingly — most
     *  modals carry their context in the body copy, not the header. */
    subtitle?: ReactNode;
};

Modal.Header = ({ children, className = '', onClose, subtitle }: HeaderProps) => (
    <div className={`ui-modal__header ${className}`.trim()}>
        <div className="ui-modal__title-group">
            <h2 className="ui-modal__title">{children}</h2>
            {subtitle && <p className="ui-modal__subtitle">{subtitle}</p>}
        </div>
        {onClose && (
            <button type="button" className="ui-modal__close" onClick={onClose} aria-label="Close">
                <X size={18} />
            </button>
        )}
    </div>
);

// Named SFC so the rules-of-hooks linter recognises it as a component
// (sub-components attached as `Modal.Body = (…)` are not — same hook
// works fine, the linter just can't infer it from the assignment).
function ModalBody({ children, className = '' }: SlotProps) {
    const { ref } = useScrollFade<HTMLDivElement>({ axis: 'y', fadeSize: 'lg' });
    return (
        <div ref={ref} className={`ui-modal__body ${className}`.trim()}>{children}</div>
    );
}
Modal.Body = ModalBody;

Modal.Footer = ({ children, className = '' }: SlotProps) => (
    <div className={`ui-modal__footer ${className}`.trim()}>{children}</div>
);

export default Modal;
